import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { catChangeRequiresLineItemResync } from "@rebuild/shared";
import type { Estimation } from "@/interfaces/estimation.interface";
import { EstimateType } from "@/enums/estimate-type";
import { useCat3LineHighlightOptional } from "@/components/estimation-details/cat3-sync/Cat3LineHighlightContext";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CAT_VALUES = [1, 2, 3] as const;
const CLASS_VALUES = [1, 2, 3, 4] as const;

// Default to enabled when the env var is unset; set to "false" to lock the dropdowns.
const CAT_CLASS_EDITABLE = import.meta.env.VITE_MITIGATION_CAT_CLASS_EDITABLE === "true";

function catChanged(prevCat: number | undefined, nextCat: number): boolean {
  return prevCat !== nextCat;
}

interface MitigationCatClassFieldsProps {
  estimation: Estimation | undefined;
  updateEstimationDetails: (_estimation: Estimation, field: keyof Estimation, clientOnly?: boolean) => Promise<boolean>;
  disabled?: boolean;
  disabledTooltip?: string;
}

export function MitigationCatClassFields({
  estimation,
  updateEstimationDetails,
  disabled = false,
  disabledTooltip,
}: MitigationCatClassFieldsProps) {
  const [catConfirmOpen, setCatConfirmOpen] = useState(false);
  const [pendingCat, setPendingCat] = useState<number | null>(null);
  // "confirm" = awaiting the user's decision, "saving" = the POST is in flight, "syncing" = the change
  // is saved and the worker is recomputing line items (the dialog stays open until that finishes),
  // "failed" = the worker job failed and the user has to retry or back out.
  const [catConfirmPhase, setCatConfirmPhase] = useState<"confirm" | "saving" | "syncing" | "failed">("confirm");

  const md = estimation?.mitigationDetails;

  const catSync = useCat3LineHighlightOptional();
  const isCatSyncPending = catSync?.isCatSyncPending ?? false;
  const catSyncFailure = catSync?.catSyncFailure ?? null;
  // Whether this cat change will offload a line-item recompute to the worker. Captured at confirm time,
  // before any optimistic cache update mutates md.cat.
  const willCatSyncRef = useRef(false);

  const commitMitigationDetails = useCallback(
    async (next: NonNullable<Estimation["mitigationDetails"]>) => {
      if (!estimation) return false;
      const newEstimation = structuredClone(estimation);
      newEstimation.mitigationDetails = next;
      return updateEstimationDetails(newEstimation, "mitigationDetails");
    },
    [estimation, updateEstimationDetails],
  );

  const onCatSelectChange = useCallback(
    (value: string) => {
      const nextCat = Number.parseInt(value, 10);
      const prevCat = md?.cat;
      // Invalid parse, or Radix re-sync no-op (do not POST).
      if (Number.isNaN(nextCat) || (prevCat !== undefined && Number(prevCat) === nextCat)) return;

      if (catChanged(prevCat, nextCat)) {
        setPendingCat(nextCat);
        setCatConfirmOpen(true);
        return;
      }
      void commitMitigationDetails({
        ...(md ?? {}),
        cat: nextCat,
      });
    },
    [commitMitigationDetails, md],
  );

  const closeCatConfirm = useCallback(() => {
    setCatConfirmOpen(false);
    setPendingCat(null);
    setCatConfirmPhase("confirm");
    willCatSyncRef.current = false;
    catSync?.dismissCatSyncFailure();
  }, [catSync]);

  const onConfirmCatChange = useCallback(async () => {
    if (pendingCat === null) return;
    // Drop any failure from a previous attempt so a retry can't be resolved against it.
    catSync?.dismissCatSyncFailure();

    const prevCat = md?.cat;
    // Mirrors the backend/worker gate: a line-item recompute (offloaded to the worker) runs only for
    // MITIGATION estimates when the cat changes to or from a contamination cat (2 or 3). It also needs
    // the highlight provider in scope to drive the shared pending flag; without one the recompute runs
    // inline and is already done by the time the commit resolves, so there is nothing to wait for.
    willCatSyncRef.current =
      !!catSync && estimation?.estimateType === EstimateType.MITIGATION && catChangeRequiresLineItemResync(prevCat, pendingCat);

    setCatConfirmPhase("saving");
    const ok = await commitMitigationDetails({
      ...(md ?? {}),
      cat: pendingCat,
    });
    if (!ok) {
      // Keep the dialog open so the user can retry.
      setCatConfirmPhase("confirm");
      return;
    }
    if (willCatSyncRef.current) {
      // Hold the dialog open in the "updating line items" state; the effect below closes it once the
      // worker job completes (isCatSyncPending: true → false).
      setCatConfirmPhase("syncing");
    } else {
      closeCatConfirm();
    }
  }, [pendingCat, md, catSync, estimation?.estimateType, commitMitigationDetails, closeCatConfirm]);

  // Resolve the dialog once the worker's line-item recompute reaches a terminal state: close on
  // success, switch to the error view on failure. We only enter "syncing" after beginCatSync has
  // already flipped the shared pending flag true, so a false reading here always means "done".
  useEffect(() => {
    if (catConfirmPhase !== "syncing" || isCatSyncPending) return;
    if (catSyncFailure) {
      setCatConfirmPhase("failed");
      return;
    }
    closeCatConfirm();
  }, [catConfirmPhase, isCatSyncPending, catSyncFailure, closeCatConfirm]);

  const onCatConfirmOpenChange = useCallback(
    (open: boolean) => {
      // Block dismissal while the change is saving or the worker is still recomputing line items —
      // but never while showing the failure, or the user would be stuck in the dialog.
      if (!open && catConfirmPhase !== "confirm" && catConfirmPhase !== "failed") return;
      if (!open) {
        closeCatConfirm();
        return;
      }
      setCatConfirmOpen(open);
    },
    [catConfirmPhase, closeCatConfirm],
  );

  const onClassSelectChange = useCallback(
    (value: string) => {
      const nextClass = Number.parseInt(value, 10);
      if (Number.isNaN(nextClass) || (md?.class !== undefined && Number(md.class) === nextClass)) return;
      void commitMitigationDetails({
        ...(md ?? {}),
        class: nextClass,
      });
    },
    [commitMitigationDetails, md],
  );

  const catSelectValue = md?.cat !== undefined ? String(md.cat) : undefined;
  const classSelectValue = md?.class !== undefined ? String(md.class) : undefined;

  return (
    <>
      <div className="grid grid-cols-3">
        <div className="flex w-[100px] flex-col justify-center text-xs font-medium whitespace-nowrap text-[#71717A]">
          <label htmlFor="mitigation-cat-select">Cat</label>
        </div>
        <div className="col-span-2">
          {CAT_CLASS_EDITABLE ? (
            <Select value={catSelectValue} onValueChange={onCatSelectChange} disabled={disabled}>
              <SelectTrigger
                id="mitigation-cat-select"
                className="h-7 max-w-[120px] text-sm"
                title={disabled ? disabledTooltip : undefined}
              >
                <SelectValue placeholder="-" />
              </SelectTrigger>
              <SelectContent>
                {CAT_VALUES.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span id="mitigation-cat-select" className="text-sm">
              {catSelectValue ?? "-"}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3">
        <div className="flex w-[100px] flex-col justify-center text-xs font-medium whitespace-nowrap text-[#71717A]">
          <label htmlFor="mitigation-class-select">Class</label>
        </div>
        <div className="col-span-2">
          {CAT_CLASS_EDITABLE ? (
            <Select value={classSelectValue} onValueChange={onClassSelectChange} disabled={disabled}>
              <SelectTrigger
                id="mitigation-class-select"
                className="h-7 max-w-[120px] text-sm"
                title={disabled ? disabledTooltip : undefined}
              >
                <SelectValue placeholder="-" />
              </SelectTrigger>
              <SelectContent>
                {CLASS_VALUES.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span id="mitigation-class-select" className="text-sm">
              {classSelectValue ?? "-"}
            </span>
          )}
        </div>
      </div>

      <AlertDialog open={catConfirmOpen} onOpenChange={onCatConfirmOpenChange}>
        <AlertDialogContent className="!bg-white text-black">
          {catConfirmPhase === "syncing" ? (
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lg font-medium text-gray-800">Updating line items…</AlertDialogTitle>
              <AlertDialogDescription className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                This might take a second.
              </AlertDialogDescription>
            </AlertDialogHeader>
          ) : catConfirmPhase === "failed" ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-lg font-medium text-gray-800">Couldn&apos;t update line items</AlertDialogTitle>
                <AlertDialogDescription className="mt-2 text-sm text-gray-600">
                  The category change wasn&apos;t applied and your line items are unchanged. You can try again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="mt-6 flex flex-row-reverse gap-2">
                <Button type="button" className="!bg-black !text-white hover:bg-gray-800" onClick={() => void onConfirmCatChange()}>
                  Try again
                </Button>
                <Button type="button" variant="outline" onClick={closeCatConfirm}>
                  Close
                </Button>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-lg font-medium text-gray-800">
                  {pendingCat !== null ? `Update line items for Cat ${pendingCat}?` : "Update line items?"}
                </AlertDialogTitle>
                <AlertDialogDescription className="mt-2 text-sm text-gray-600">
                  Applicable line items will be updated to reflect the new category. Any edits you made after initial processing may be
                  lost.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="mt-6 flex flex-row-reverse gap-2">
                <Button
                  type="button"
                  className="!bg-black !text-white hover:bg-gray-800"
                  disabled={catConfirmPhase === "saving"}
                  onClick={() => void onConfirmCatChange()}
                >
                  {catConfirmPhase === "saving" ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving…
                    </span>
                  ) : (
                    "Continue"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={catConfirmPhase === "saving"}
                  onClick={() => onCatConfirmOpenChange(false)}
                >
                  Cancel
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
